import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Popover, Box, Typography, CircularProgress } from "@mui/material";

const DictionaryTooltip = () => {
  const [selectedText, setSelectedText] = useState("");
  const [definition, setDefinition] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const popoverRef = useRef(null); // Reference to the popover

  useEffect(() => {
    const handleSelection = (event) => {
      const selection = window.getSelection();
      const selectedText = selection.toString().trim();

      if (selectedText && selectedText.length > 0) {
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();

        setSelectedText(selectedText);
        setAnchorEl({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
        });

        fetchDefinition(selectedText);
      }
    };

    document.addEventListener("mouseup", handleSelection);

    return () => {
      document.removeEventListener("mouseup", handleSelection);
    };
  }, []);

  const fetchDefinition = async (word) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.get(`https://api.dictionaryapi.dev/api/v2/entries/en/${word}`);

      if (response.data && response.data.length > 0) {
        const firstMeaning = response.data[0].meanings[0];
        setDefinition({
          word: response.data[0].word,
          phonetic: response.data[0].phonetic || "",
          partOfSpeech: firstMeaning.partOfSpeech,
          definition: firstMeaning.definitions[0].definition,
        });
      }
    } catch (err) {
      setError("No definition found");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedText("");
    setDefinition(null);
  };

  const open = Boolean(anchorEl);

  // Close the popover when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        handleClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      {open && (
        <Popover
          open={open}
          anchorReference="anchorPosition"
          anchorPosition={anchorEl}
          onClose={handleClose}
          PaperProps={{
            style: { padding: "12px", maxWidth: "300px" },
          }}
          ref={popoverRef} // Attach the ref here
        >
          <Box display="flex" flexDirection="column" gap={2}>
            {isLoading ? (
              <Box display="flex" justifyContent="center">
                <CircularProgress size={24} />
              </Box>
            ) : error ? (
              <Typography color="error">{error}</Typography>
            ) : definition && (
              <>
                <Typography variant="h6" fontWeight="bold">
                  {definition.word}
                </Typography>
                <Typography variant="subtitle2" color="textSecondary">
                  {definition.phonetic}
                </Typography>
                <Typography variant="body1" color="textSecondary" fontStyle="italic">
                  {definition.partOfSpeech}
                </Typography>
                <Typography variant="body2">{definition.definition}</Typography>
              </>
            )}
          </Box>
        </Popover>
      )}
    </>
  );
};

export default DictionaryTooltip;